import { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Menu, Transition } from "@headlessui/react";
import {
  Check,
  ChevronDown,
  Copy,
  ExternalLink,
  Filter,
  FolderInput,
  Grid3X3,
  Link as LinkIcon,
  List,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  Share2,
  SortAsc,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ReactQuill from "react-quill";
import TagsInput from "react-tagsinput";
import "react-quill/dist/quill.snow.css";
import "react-tagsinput/react-tagsinput.css";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";

import "./Bookmarks.scss";
import BookmarkPreview from "../../assets/icons/bookmarksPreviews.svg";
import ShareCard from "../../shades/cards/shareCard";
import MoveModal from "../../shades/cards/includes/MoveModal";
import { toastify } from "../../components/Toast";

const SORT_OPTIONS = [
  { label: "Newest", value: "Created_Date" },
  { label: "Recent activity", value: "Recent_Activity" },
  { label: "A-Z", value: "Ascending_Order" },
  { label: "Z-A", value: "Descending_Order" },
];

const FILTER_OPTIONS = [
  { label: "All bookmarks", value: "all" },
  { label: "Starred only", value: "starred" },
  { label: "With tags", value: "with-tags" },
  { label: "Without tags", value: "without-tags" },
];

const quillModules = {
  clipboard: { matchVisual: false },
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "link",
];

function getErrorMessage(error) {
  return error?.response?.data?.message || "Something went wrong. Please try again.";
}

function stripHtml(value = "") {
  if (!value) return "";
  const element = document.createElement("div");
  element.innerHTML = value;
  return element.textContent || element.innerText || "";
}

function getDomain(url = "") {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url.replace(/^https?:\/\//, "").split("/")[0];
  }
}

function formatDate(date) {
  if (!date) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(date));
}

function getResolvedAppTheme() {
  const selectedTheme = localStorage.getItem("theme");

  if (selectedTheme === "dark" || selectedTheme === "light") {
    return selectedTheme;
  }

  if (document.documentElement.classList.contains("dark")) {
    return "dark";
  }

  if (document.documentElement.classList.contains("light")) {
    return "light";
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function BookmarksGrid() {
  const navigate = useNavigate();
  const savedSort = localStorage.getItem("cardFilter") || "Created_Date";

  const [bookmarkCards, setBookmarkCards] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [viewMode, setViewMode] = useState("list");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [sortBy, setSortBy] = useState(savedSort);
  const [filterMode, setFilterMode] = useState("all");
  const [filterBucketId, setFilterBucketId] = useState("");
  const [filterTagId, setFilterTagId] = useState("");
  const [appTheme, setAppTheme] = useState(getResolvedAppTheme);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [bookmarkUrl, setBookmarkUrl] = useState("");
  const [selectedBucket, setSelectedBucket] = useState("");
  const [tags, setTags] = useState([]);
  const [notes, setNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [deleteIds, setDeleteIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [shareBookmarkId, setShareBookmarkId] = useState("");
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isMoveOpen, setIsMoveOpen] = useState(false);
  const [copyIds, setCopyIds] = useState([]);
  const [copyBucketId, setCopyBucketId] = useState("");
  const [isCopyOpen, setIsCopyOpen] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  const isDark = appTheme === "dark";

  const cardsRefresh = () => setRefreshVersion((version) => version + 1);

  useEffect(() => {
    const syncTheme = () => {
      setAppTheme(getResolvedAppTheme());
    };
    const systemTheme = window.matchMedia("(prefers-color-scheme: dark)");
    const observer = new MutationObserver(syncTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    window.addEventListener("storage", syncTheme);
    systemTheme.addEventListener("change", syncTheme);
    return () => {
      observer.disconnect();
      window.removeEventListener("storage", syncTheme);
      systemTheme.removeEventListener("change", syncTheme);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchBookmarks = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.post(
          `${process.env.REACT_APP_API}cards/cards-with-bookmark`,
          {
            filterValue: sortBy,
            limit: 10000,
            bucketId: filterBucketId,
            tagId: filterTagId,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) {
          const cards = response?.data?.data;
          setBookmarkCards(Array.isArray(cards) ? cards : []);
        }
      } catch (error) {
        if (!cancelled) toastify("error", getErrorMessage(error));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchBookmarks();
    return () => {
      cancelled = true;
    };
  }, [sortBy, filterBucketId, filterTagId, refreshVersion]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    Promise.all([
      axios.get(`${process.env.REACT_APP_API}buckets/user-buckets`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      axios.get(`${process.env.REACT_APP_API}tags/list`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ])
      .then(([bucketResponse, tagResponse]) => {
        setBuckets(bucketResponse?.data?.data?.bucketList || []);
        setTagOptions(tagResponse?.data?.data || []);
      })
      .catch((error) => console.error("Unable to load bookmark filters", error));
  }, []);

  useEffect(() => {
    setSelectedItems((selected) =>
      selected.filter((id) => bookmarkCards.some((card) => card._id === id))
    );
  }, [bookmarkCards]);

  const bucketChoices = useMemo(
    () =>
      buckets.flatMap((bucket) => [
        { id: bucket._id, name: bucket.name },
        ...(bucket.subBuckets || []).map((subBucket) => ({
          id: subBucket._id,
          name: `${bucket.name} / ${subBucket.name}`,
        })),
      ]),
    [buckets]
  );

  const visibleBookmarks = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return bookmarkCards.filter((card) => {
      const tagCount = card?.tags?.length || 0;
      const matchesMode =
        filterMode === "all" ||
        (filterMode === "starred" && card.starred) ||
        (filterMode === "with-tags" && tagCount > 0) ||
        (filterMode === "without-tags" && tagCount === 0);
      if (!matchesMode) return false;
      if (!query) return true;
      return [
        card.title,
        card?.postDetail?.title,
        card?.content?.sourceUrl,
        stripHtml(card?.content?.html),
        ...(card?.tags || []).map((tag) => tag?.name || tag),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [bookmarkCards, filterMode, searchQuery]);

  const selectedVisibleCount = visibleBookmarks.filter((card) =>
    selectedItems.includes(card._id)
  ).length;
  const allVisibleSelected =
    visibleBookmarks.length > 0 && selectedVisibleCount === visibleBookmarks.length;
  const activeFilterCount =
    (filterMode !== "all" ? 1 : 0) + (filterBucketId ? 1 : 0) + (filterTagId ? 1 : 0);

  const resetAddForm = () => {
    setBookmarkUrl("");
    setSelectedBucket("");
    setTags([]);
    setNotes("");
  };

  const closeAddModal = () => {
    setIsAddOpen(false);
    resetAddForm();
  };

  const addBookmark = async () => {
    if (!bookmarkUrl.trim()) {
      toastify("error", "Please enter a bookmark URL.");
      return;
    }
    if (!selectedBucket) {
      toastify("error", "Please select a bucket.");
      return;
    }

    setIsSaving(true);
    const token = localStorage.getItem("token");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const formData = new FormData();
    formData.append("tags", JSON.stringify(tags.map((tag) => ({ name: tag }))));
    formData.append("note", notes || "");
    formData.append("title", "title");
    formData.append("userId", userData._id || "");
    formData.append("bucketId", selectedBucket);
    formData.append("cardType", "bookmark");
    formData.append(
      "content",
      JSON.stringify({ html: "test", sourceUrl: bookmarkUrl.trim(), imageUrl: [] })
    );
    formData.append("postDetail", JSON.stringify({ title: "sdasdas", imageUrl: "sdasdsadsad" }));

    try {
      const response = await axios.post(`${process.env.REACT_APP_API}cards/add`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toastify("success", response?.data?.message);
      closeAddModal();
      cardsRefresh();
    } catch (error) {
      toastify("error", getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const toggleSelection = (id) => {
    setSelectedItems((selected) =>
      selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id]
    );
  };

  const toggleSelectAll = () => {
    const visibleIds = visibleBookmarks.map((card) => card._id);
    setSelectedItems((selected) =>
      allVisibleSelected
        ? selected.filter((id) => !visibleIds.includes(id))
        : [...new Set([...selected, ...visibleIds])]
    );
  };

  const updateStar = async (card) => {
    const nextStarred = !card.starred;
    setBookmarkCards((cards) =>
      cards.map((item) => (item._id === card._id ? { ...item, starred: nextStarred } : item))
    );
    try {
      const token = localStorage.getItem("token");
      const response = await axios.patch(
        `${process.env.REACT_APP_API}cards/update`,
        { cardId: card._id, starred: nextStarred },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response?.data?.message) toastify("success", response.data.message);
    } catch (error) {
      setBookmarkCards((cards) =>
        cards.map((item) => (item._id === card._id ? { ...item, starred: card.starred } : item))
      );
      toastify("error", getErrorMessage(error));
    }
  };

  const copyPublicLink = async (id) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API}cards/public-url`,
        { itemId: id, isPublic: true, itemType: "Card" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response?.data?.data) {
        await navigator.clipboard.writeText(response.data.data);
        toastify("success", response?.data?.message || "Link copied.");
      }
    } catch (error) {
      toastify("error", getErrorMessage(error));
    }
  };

  const deleteBookmarks = async () => {
    if (!deleteIds.length) return;
    setIsDeleting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`${process.env.REACT_APP_API}cards/delete`, {
        data: { deletedIds: deleteIds },
        headers: { Authorization: `Bearer ${token}` },
      });
      toastify("success", response?.data?.message);
      setSelectedItems((selected) => selected.filter((id) => !deleteIds.includes(id)));
      setDeleteIds([]);
      cardsRefresh();
    } catch (error) {
      toastify("error", getErrorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  };

  const openMoveModal = (ids) => {
    setSelectedItems(ids);
    setIsMoveOpen(true);
  };

  const closeMoveModal = () => {
    setSelectedItems([]);
    setIsMoveOpen(false);
  };

  const openCopyModal = (ids) => {
    setCopyIds(ids);
    setCopyBucketId("");
    setIsCopyOpen(true);
  };

  const copyBookmarksToBucket = async () => {
    if (!copyBucketId) {
      toastify("error", "Please select a destination bucket.");
      return;
    }
    setIsCopying(true);
    try {
      const token = localStorage.getItem("token");
      const responses = await Promise.all(
        copyIds.map((cardId) =>
          axios.patch(
            `${process.env.REACT_APP_API}cards/update`,
            { optionType: "CopyTo", destinationBucketID: copyBucketId, cardId },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      toastify("success", responses[0]?.data?.message || "Bookmark copied.");
      setIsCopyOpen(false);
      setCopyIds([]);
      setSelectedItems([]);
      cardsRefresh();
    } catch (error) {
      toastify("error", getErrorMessage(error));
    } finally {
      setIsCopying(false);
    }
  };

  const openShare = (id) => {
    setShareBookmarkId(id);
    setIsShareOpen(true);
  };

  const openDetails = (id) => navigate(`/app/bookmark-detail/${id}`);

  const renderCardMenu = (card) => (
    <Menu as="div" className="relative">
      <Menu.Button
        className="bookmark-icon-button"
        onClick={(event) => event.stopPropagation()}
        aria-label={`More actions for ${card.title}`}
      >
        <MoreVertical className="h-4 w-4" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="bookmark-menu bookmark-card-menu">
          <Menu.Item>
            {({ active }) => (
              <button
                className={active ? "active" : ""}
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/app/CardEdit/${card._id}`);
                }}
              >
                <Pencil /> Edit bookmark
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                className={active ? "active" : ""}
                onClick={(event) => {
                  event.stopPropagation();
                  openMoveModal([card._id]);
                }}
              >
                <FolderInput /> Move to bucket
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                className={active ? "active" : ""}
                onClick={(event) => {
                  event.stopPropagation();
                  openCopyModal([card._id]);
                }}
              >
                <Copy /> Copy to bucket
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                className={active ? "active" : ""}
                onClick={(event) => {
                  event.stopPropagation();
                  openShare(card._id);
                }}
              >
                <Share2 /> Share
              </button>
            )}
          </Menu.Item>
          <div className="bookmark-menu-separator" />
          <Menu.Item>
            {({ active }) => (
              <button
                className={`danger ${active ? "active" : ""}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setDeleteIds([card._id]);
                }}
              >
                <Trash2 /> Delete
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );

  const renderGridCard = (card) => {
    const imageUrl = card?.content?.imageUrl?.[0]?.url || BookmarkPreview;
    const sourceUrl = card?.content?.sourceUrl || "";
    const isSelected = selectedItems.includes(card._id);

    return (
      <article
        key={card._id}
        className={`bookmark-grid-card group ${isSelected ? "selected" : ""}`}
        onClick={() => openDetails(card._id)}
      >
        <button
          type="button"
          className={`bookmark-select ${isSelected ? "selected" : ""}`}
          onClick={(event) => {
            event.stopPropagation();
            toggleSelection(card._id);
          }}
          aria-label={isSelected ? `Deselect ${card.title}` : `Select ${card.title}`}
        >
          {isSelected && <Check className="h-2.5 w-2.5" />}
        </button>
        <button
          type="button"
          className={`bookmark-grid-star ${card.starred ? "starred" : ""}`}
          onClick={(event) => {
            event.stopPropagation();
            updateStar(card);
          }}
          aria-label={card.starred ? "Remove from starred" : "Add to starred"}
        >
          <Star className={card.starred ? "fill-current" : ""} />
        </button>
        <div className="bookmark-grid-image">
          <img src={imageUrl} alt="" />
        </div>
        <div className="bookmark-grid-content">
          <h3>{card.title || card?.postDetail?.title || "Untitled bookmark"}</h3>
          <div>{getDomain(sourceUrl) || "Saved bookmark"}</div>
        </div>
      </article>
    );
  };

  const renderListCard = (card) => {
    const imageUrl = card?.content?.imageUrl?.[0]?.url || BookmarkPreview;
    const sourceUrl = card?.content?.sourceUrl || "";
    const isSelected = selectedItems.includes(card._id);
    const cardTags = card?.tags || [];

    return (
      <article
        key={card._id}
        className={`bookmark-list-card group ${isSelected ? "selected" : ""}`}
        onClick={() => openDetails(card._id)}
      >
        <button
          type="button"
          className={`bookmark-list-select ${isSelected ? "selected" : ""}`}
          onClick={(event) => {
            event.stopPropagation();
            toggleSelection(card._id);
          }}
          aria-label={isSelected ? `Deselect ${card.title}` : `Select ${card.title}`}
        >
          {isSelected && <Check className="h-3 w-3" />}
        </button>
        <div className="bookmark-list-image">
          <img src={imageUrl} alt="" />
        </div>
        <div className="bookmark-list-copy">
          <h3>{card.title || card?.postDetail?.title || "Untitled bookmark"}</h3>
          <p>{sourceUrl || stripHtml(card?.content?.html) || "Saved bookmark"}</p>
        </div>
        <div className="bookmark-list-tags">
          {cardTags.length ? (
            cardTags.slice(0, 2).map((tagItem) => (
              <span key={tagItem?._id || tagItem?.name || tagItem}>
                {tagItem?.name || tagItem}
              </span>
            ))
          ) : (
            <span className="empty">No Tags</span>
          )}
        </div>
        <time>{formatDate(card.createdAt)}</time>
        <div className="bookmark-list-actions">
          <button
            type="button"
            className={`bookmark-icon-button ${card.starred ? "starred" : ""}`}
            onClick={(event) => {
              event.stopPropagation();
              updateStar(card);
            }}
            aria-label={card.starred ? "Remove from starred" : "Add to starred"}
          >
            <Star className={card.starred ? "fill-current" : ""} />
          </button>
          <button
            type="button"
            className="bookmark-icon-button"
            onClick={(event) => {
              event.stopPropagation();
              window.open(sourceUrl, "_blank", "noopener,noreferrer");
            }}
            aria-label="Open source"
            disabled={!sourceUrl}
          >
            <ExternalLink />
          </button>
          <button
            type="button"
            className="bookmark-icon-button"
            onClick={(event) => {
              event.stopPropagation();
              copyPublicLink(card._id);
            }}
            aria-label="Copy public link"
          >
            <LinkIcon />
          </button>
          {renderCardMenu(card)}
        </div>
      </article>
    );
  };

  return (
    <div className={`bookmarks-redesign ${isDark ? "is-dark" : "is-light"}`}>
      <header className="bookmarks-titlebar">
        <div className="bookmarks-title-wrap">
          <h1>Bookmarks</h1>
          <span className="bookmarks-count">{bookmarkCards.length}</span>
        </div>
        <button
          className="bookmark-add-button"
          type="button"
          aria-label="Add bookmark"
          onClick={() => setIsAddOpen(true)}
        >
          <Plus />
          <span>Add bookmark</span>
        </button>
      </header>

      <section className="bookmarks-toolbar" aria-label="Bookmark controls">
        <div className="bookmarks-toolbar-main">
          <label className="bookmarks-search">
            <Search />
            <input
              type="search"
              placeholder="Search bookmarks..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {searchQuery && (
              <button type="button" onClick={() => setSearchQuery("")} aria-label="Clear search">
                <X />
              </button>
            )}
          </label>

          <Menu as="div" className="relative">
            <Menu.Button className={`bookmark-toolbar-button ${activeFilterCount ? "active" : ""}`}>
              <Filter />
              Filters
              {activeFilterCount > 0 && <span>{activeFilterCount}</span>}
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="bookmark-menu bookmark-filter-menu">
                <div className="bookmark-menu-label">Show</div>
                {FILTER_OPTIONS.map((option) => (
                  <Menu.Item key={option.value}>
                    {({ active }) => (
                      <button
                        className={active ? "active" : ""}
                        onClick={() => setFilterMode(option.value)}
                      >
                        <span className={`bookmark-menu-check ${filterMode === option.value ? "checked" : ""}`}>
                          {filterMode === option.value && <Check />}
                        </span>
                        {option.label}
                      </button>
                    )}
                  </Menu.Item>
                ))}
                <div className="bookmark-menu-separator" />
                <label className="bookmark-filter-select">
                  <span>Bucket</span>
                  <select value={filterBucketId} onChange={(event) => setFilterBucketId(event.target.value)}>
                    <option value="">All buckets</option>
                    {bucketChoices.map((bucket) => (
                      <option key={bucket.id} value={bucket.id}>{bucket.name}</option>
                    ))}
                  </select>
                </label>
                <label className="bookmark-filter-select">
                  <span>Tag</span>
                  <select value={filterTagId} onChange={(event) => setFilterTagId(event.target.value)}>
                    <option value="">All tags</option>
                    {tagOptions.map((tagOption) => (
                      <option key={tagOption._id} value={tagOption._id}>{tagOption.name}</option>
                    ))}
                  </select>
                </label>
                <button
                  className="bookmark-clear-filters"
                  type="button"
                  onClick={() => {
                    setFilterMode("all");
                    setFilterBucketId("");
                    setFilterTagId("");
                  }}
                  disabled={!activeFilterCount}
                >
                  Clear filters
                </button>
                <div className="bookmark-menu-separator" />
                <button type="button" onClick={toggleSelectAll} disabled={!visibleBookmarks.length}>
                  <span className={`bookmark-menu-check ${allVisibleSelected ? "checked" : ""}`}>
                    {allVisibleSelected && <Check />}
                  </span>
                  {allVisibleSelected ? "Unselect all" : "Select all"}
                </button>
                <button className="bookmark-menu-add" type="button" onClick={() => setIsAddOpen(true)}>
                  <Plus /> Add bookmark
                </button>
              </Menu.Items>
            </Transition>
          </Menu>

          <Menu as="div" className="relative">
            <Menu.Button className="bookmark-toolbar-button">
              <SortAsc />
              {SORT_OPTIONS.find((option) => option.value === sortBy)?.label || "Sort"}
              <ChevronDown />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="bookmark-menu bookmark-sort-menu">
                {SORT_OPTIONS.map((option) => (
                  <Menu.Item key={option.value}>
                    {({ active }) => (
                      <button
                        className={active ? "active" : ""}
                        onClick={() => {
                          setSortBy(option.value);
                          localStorage.setItem("cardFilter", option.value);
                        }}
                      >
                        {option.label}
                        {sortBy === option.value && <Check />}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>

        </div>

        <div className="bookmark-view-toggle">
          <button
            type="button"
            className={viewMode === "grid" ? "active" : ""}
            onClick={() => setViewMode("grid")}
            aria-label="Grid view"
          >
            <Grid3X3 />
          </button>
          <button
            type="button"
            className={viewMode === "list" ? "active" : ""}
            onClick={() => setViewMode("list")}
            aria-label="List view"
          >
            <List />
          </button>
        </div>
      </section>

      {selectedItems.length > 0 && (
        <section className="bookmark-selection-bar">
          <span>{selectedItems.length} selected</span>
          <button type="button" onClick={() => openMoveModal(selectedItems)}>
            <FolderInput /> Move to
          </button>
          <button type="button" onClick={() => openCopyModal(selectedItems)}>
            <Copy /> Copy to
          </button>
          <button className="danger" type="button" onClick={() => setDeleteIds(selectedItems)}>
            <Trash2 /> Delete
          </button>
          <button className="close" type="button" onClick={() => setSelectedItems([])} aria-label="Clear selection">
            <X />
          </button>
        </section>
      )}

      <main className="bookmarks-content">
        {isLoading ? (
          <div className="App-loading bookmarks-logo-loading">
            <div className="App-logo" title="Zimzee" />
          </div>
        ) : visibleBookmarks.length ? (
          viewMode === "grid" ? (
            <div className="bookmarks-grid">{visibleBookmarks.map(renderGridCard)}</div>
          ) : (
            <div className="bookmarks-list">{visibleBookmarks.map(renderListCard)}</div>
          )
        ) : (
          <div className="bookmarks-empty">
            <div><Search /></div>
            <h2>No bookmarks found</h2>
            <p>{searchQuery || activeFilterCount ? "Try changing your search or filters." : "Add your first bookmark to see it here."}</p>
            {!searchQuery && !activeFilterCount && (
              <button type="button" onClick={() => setIsAddOpen(true)}><Plus /> Add bookmark</button>
            )}
          </div>
        )}
      </main>

      <Transition appear show={isAddOpen} as={Fragment}>
        <Dialog
          as="div"
          className={`bookmark-dialog-root ${isDark ? "is-dark" : "is-light"}`}
          onClose={closeAddModal}
        >
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="bookmark-dialog-backdrop" />
          </Transition.Child>
          <div className="bookmark-dialog-scroll">
            <div className="bookmark-dialog-center">
              <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="bookmark-dialog bookmark-add-dialog">
                  <div className="bookmark-dialog-header">
                    <div>
                      <Dialog.Title>Add bookmark</Dialog.Title>
                      <p>Save a website to one of your buckets.</p>
                    </div>
                    <button type="button" onClick={closeAddModal} aria-label="Close"><X /></button>
                  </div>
                  <div className="bookmark-form">
                    <label>
                      <span>Website URL</span>
                      <input type="url" placeholder="https://example.com" value={bookmarkUrl} onChange={(event) => setBookmarkUrl(event.target.value)} autoFocus />
                    </label>
                    <label>
                      <span>Bucket</span>
                      <select value={selectedBucket} onChange={(event) => setSelectedBucket(event.target.value)}>
                        <option value="">Select a bucket</option>
                        {bucketChoices.map((bucket) => <option key={bucket.id} value={bucket.id}>{bucket.name}</option>)}
                      </select>
                    </label>
                    <label>
                      <span>Tags</span>
                      <TagsInput value={tags} onChange={setTags} inputProps={{ placeholder: tags.length ? "" : "Add tags" }} />
                    </label>
                    {tagOptions.length > 0 && (
                      <div className="bookmark-suggested-tags">
                        {tagOptions.filter((option) => !tags.includes(option.name)).slice(0, 8).map((option) => (
                          <button type="button" key={option._id} onClick={() => setTags((current) => [...current, option.name])}>
                            <Plus /> {option.name}
                          </button>
                        ))}
                      </div>
                    )}
                    <label>
                      <span>Notes</span>
                      <div className="bookmark-note-editor">
                        <ReactQuill theme="snow" modules={quillModules} formats={quillFormats} value={notes} onChange={setNotes} placeholder="Add notes..." />
                      </div>
                    </label>
                  </div>
                  <div className="bookmark-dialog-footer">
                    <button type="button" className="secondary" onClick={closeAddModal}>Cancel</button>
                    <button type="button" className="primary" onClick={addBookmark} disabled={isSaving}>{isSaving ? "Saving..." : "Save bookmark"}</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition appear show={deleteIds.length > 0} as={Fragment}>
        <Dialog
          as="div"
          className={`bookmark-dialog-root ${isDark ? "is-dark" : "is-light"}`}
          onClose={() => !isDeleting && setDeleteIds([])}
        >
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="bookmark-dialog-backdrop" />
          </Transition.Child>
          <div className="bookmark-dialog-scroll">
            <div className="bookmark-dialog-center">
              <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="bookmark-dialog bookmark-confirm-dialog">
                  <div className="bookmark-danger-icon"><Trash2 /></div>
                  <Dialog.Title>Delete {deleteIds.length === 1 ? "bookmark" : `${deleteIds.length} bookmarks`}?</Dialog.Title>
                  <p>This action cannot be undone.</p>
                  <div className="bookmark-dialog-footer">
                    <button type="button" className="secondary" onClick={() => setDeleteIds([])} disabled={isDeleting}>Cancel</button>
                    <button type="button" className="danger" onClick={deleteBookmarks} disabled={isDeleting}>{isDeleting ? "Deleting..." : "Delete"}</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition appear show={isCopyOpen} as={Fragment}>
        <Dialog
          as="div"
          className={`bookmark-dialog-root ${isDark ? "is-dark" : "is-light"}`}
          onClose={() => !isCopying && setIsCopyOpen(false)}
        >
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="bookmark-dialog-backdrop" />
          </Transition.Child>
          <div className="bookmark-dialog-scroll">
            <div className="bookmark-dialog-center">
              <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="bookmark-dialog bookmark-copy-dialog">
                  <div className="bookmark-dialog-header">
                    <div>
                      <Dialog.Title>Copy to bucket</Dialog.Title>
                      <p>Choose where to copy {copyIds.length === 1 ? "this bookmark" : "these bookmarks"}.</p>
                    </div>
                    <button type="button" onClick={() => setIsCopyOpen(false)} aria-label="Close"><X /></button>
                  </div>
                  <label className="bookmark-copy-select">
                    <span>Destination bucket</span>
                    <select value={copyBucketId} onChange={(event) => setCopyBucketId(event.target.value)} autoFocus>
                      <option value="">Select a bucket</option>
                      {bucketChoices.map((bucket) => <option key={bucket.id} value={bucket.id}>{bucket.name}</option>)}
                    </select>
                  </label>
                  <div className="bookmark-dialog-footer">
                    <button type="button" className="secondary" onClick={() => setIsCopyOpen(false)} disabled={isCopying}>Cancel</button>
                    <button type="button" className="primary" onClick={copyBookmarksToBucket} disabled={isCopying}>{isCopying ? "Copying..." : "Copy"}</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {isShareOpen && (
        <div className="bookmark-share-backdrop" onClick={() => setIsShareOpen(false)}>
          <div className="bookmark-share-dialog" onClick={(event) => event.stopPropagation()}>
            <div className="bookmark-dialog-header">
              <h2>Share</h2>
              <button type="button" onClick={() => setIsShareOpen(false)} aria-label="Close"><X /></button>
            </div>
            <ShareCard shareBucketId={shareBookmarkId} closeSharePop={() => setIsShareOpen(false)} cardsRefresh={cardsRefresh} />
          </div>
        </div>
      )}

      {isMoveOpen && (
        <MoveModal openMove={isMoveOpen} closeModal={closeMoveModal} selectedItems={selectedItems} cardUpdate={cardsRefresh} buckets={buckets} />
      )}

      <ToastContainer />
    </div>
  );
}

export default BookmarksGrid;
